import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { SearchIcon, MapPinIcon, GlobeIcon, MessageCircleIcon, StarIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, updateDoc, arrayContains } from 'firebase/firestore';
import { db } from '../../firebase';
interface TeacherData {
  id: string;
  fullName: string;
  location: string;
  instruments: string[];
  experience?: string;
  rating?: number;
  reviewCount?: number;
  profilePictureUrl?: string;
  lessonType?: string[];
  online?: boolean;
  inPerson?: boolean;
}
export const TeacherMatchingPage = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    userData
  } = useAuth();
  const [teachers, setTeachers] = useState<TeacherData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lessonType, setLessonType] = useState<string>('both');
  const [zipCode, setZipCode] = useState<string>('');
  // Check if user is logged in and email is verified
  useEffect(() => {
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    if (!currentUser.emailVerified) {
      navigate('/email-verification');
      return;
    }
    // Check if instruments have been selected
    if (!userData?.instruments || userData.instruments.length === 0) {
      navigate('/instrument-selection');
      return;
    }
    // Mark onboarding as complete
    if (!userData?.onboardingComplete) {
      updateDoc(doc(db, 'students', currentUser.uid), {
        onboardingComplete: true,
        updatedAt: new Date().toISOString()
      });
      updateDoc(doc(db, 'users', currentUser.uid), {
        onboardingComplete: true,
        updatedAt: new Date().toISOString()
      });
    }
    // Load matching teachers
    loadTeachers();
  }, [currentUser, userData, navigate]);
  // Load teachers that match the student's selected instruments
  const loadTeachers = async () => {
    setLoading(true);
    setError('');
    try {
      if (!userData?.instruments || userData.instruments.length === 0) {
        setError('No instruments selected');
        setLoading(false);
        return;
      }
      // Get teachers that teach any of the selected instruments
      const teachersRef = collection(db, 'teachers');
      const teachersSnapshot = await getDocs(teachersRef);
      const matchingTeachers: TeacherData[] = [];
      teachersSnapshot.forEach(doc => {
        const teacherData = doc.data() as TeacherData;
        // Check if teacher teaches any of the student's selected instruments
        const hasMatchingInstrument = teacherData.instruments?.some(instrument => userData.instruments.includes(instrument));
        if (hasMatchingInstrument) {
          matchingTeachers.push({
            id: doc.id,
            ...teacherData,
            rating: teacherData.rating || 5.0,
            reviewCount: teacherData.reviewCount || 0,
            online: teacherData.lessonType?.includes('online') || true,
            inPerson: teacherData.lessonType?.includes('inPerson') || false
          });
        }
      });
      setTeachers(matchingTeachers);
    } catch (error: any) {
      console.error('Error loading teachers:', error);
      setError('Failed to load matching teachers');
    } finally {
      setLoading(false);
    }
  };
  // Filter teachers based on lesson type and location
  const filteredTeachers = teachers.filter(teacher => {
    // Filter by lesson type
    if (lessonType === 'online' && !teacher.online) return false;
    if (lessonType === 'inPerson' && !teacher.inPerson) return false;
    // Filter by zip code (if provided)
    if (lessonType === 'inPerson' && zipCode && teacher.location) {
      // Simple check if location contains the zip code
      // In a real app, you'd use a proper distance calculation
      return teacher.location.includes(zipCode);
    }
    return true;
  });
  // Navigate to message a teacher
  const messageTeacher = (teacherId: string) => {
    navigate(`/teacher-messages?teacherId=${teacherId}`);
  };
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-7412">
      <Navbar data-id="element-7413" />
      <main className="pt-20 pb-16" data-id="element-7414">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-7415">
          <div className="text-center mb-10" data-id="element-7416">
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-7417">
              Your Matching Teachers
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto" data-id="element-7418">
              We've found teachers who specialize in{' '}
              {userData?.instruments?.join(', ')}. Contact them to start your
              musical journey!
            </p>
          </div>
          {/* Filters */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8" data-id="element-7419">
            <h2 className="text-xl font-medium mb-4" data-id="element-7420">Filter Teachers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7421">
              <div data-id="element-7422">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-7423">
                  Lesson Type
                </label>
                <select value={lessonType} onChange={e => setLessonType(e.target.value)} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" data-id="element-7424">
                  <option value="both" data-id="element-7425">Both Online & In-Person</option>
                  <option value="online" data-id="element-7426">Online Only</option>
                  <option value="inPerson" data-id="element-7427">In-Person Only</option>
                </select>
              </div>
              {(lessonType === 'inPerson' || lessonType === 'both') && <div data-id="element-7428">
                  <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-7429">
                    Your Zip Code (for in-person lessons)
                  </label>
                  <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} placeholder="Enter your zip code" className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" data-id="element-7430" />
                </div>}
            </div>
          </div>
          {error && <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg mb-6 text-center" data-id="element-7431">
              {error}
            </div>}
          {/* Teacher List */}
          {loading ? <div className="text-center py-12" data-id="element-7432">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-deep-pink border-r-2 border-white/30 mb-4" data-id="element-7433"></div>
              <p data-id="element-7434">Finding your perfect teachers...</p>
            </div> : filteredTeachers.length === 0 ? <div className="bg-white/5 rounded-xl p-8 text-center" data-id="element-7435">
              <p className="text-xl mb-4" data-id="element-7436">No matching teachers found</p>
              <p className="text-gray-300 mb-6" data-id="element-7437">
                Try adjusting your filters or check back later as we add more
                teachers.
              </p>
              <button onClick={() => setLessonType('both')} className="bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-6 rounded-lg transition-colors" data-id="element-7438">
                Reset Filters
              </button>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7439">
              {filteredTeachers.map(teacher => <div key={teacher.id} className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden hover:bg-white/15 transition-colors" data-id="element-7440">
                  <div className="h-40 bg-gradient-to-r from-deep-pink/20 to-navy-900/40 relative" data-id="element-7441">
                    {teacher.profilePictureUrl ? <img src={teacher.profilePictureUrl} alt={teacher.fullName} className="w-full h-full object-cover" data-id="element-7442" /> : <div className="absolute bottom-0 left-0 p-4" data-id="element-7443">
                        <div className="bg-deep-pink/20 rounded-full p-3 w-16 h-16 flex items-center justify-center" data-id="element-7444">
                          <span className="text-2xl font-bold text-deep-pink" data-id="element-7445">
                            {teacher.fullName.charAt(0)}
                          </span>
                        </div>
                      </div>}
                  </div>
                  <div className="p-6" data-id="element-7446">
                    <div className="flex items-start justify-between mb-2" data-id="element-7447">
                      <h3 className="text-xl font-bold" data-id="element-7448">{teacher.fullName}</h3>
                      <div className="flex items-center" data-id="element-7449">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" data-id="element-7450" />
                        <span className="text-sm" data-id="element-7451">
                          {teacher.rating?.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-300 text-sm mb-3" data-id="element-7452">
                      <MapPinIcon className="h-4 w-4 mr-1" data-id="element-7453" />
                      <span data-id="element-7454">
                        {teacher.location || 'Location not specified'}
                      </span>
                    </div>
                    <div className="mb-4" data-id="element-7455">
                      <h4 className="text-sm font-medium text-gray-300 mb-1" data-id="element-7456">
                        Teaches
                      </h4>
                      <div className="flex flex-wrap gap-2" data-id="element-7457">
                        {teacher.instruments?.map((instrument, index) => <span key={index} className="bg-white/10 text-xs px-2 py-1 rounded-full" data-id="element-7458">
                            {instrument}
                          </span>)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-7459">
                      {teacher.online && <div className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-7460">
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-7461" />
                          Online
                        </div>}
                      {teacher.inPerson && <div className="bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-7462">
                          <MapPinIcon className="h-3 w-3 mr-1" data-id="element-7463" />
                          In-Person
                        </div>}
                    </div>
                    <button onClick={() => messageTeacher(teacher.id)} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center" data-id="element-7464">
                      <MessageCircleIcon className="h-4 w-4 mr-2" data-id="element-7465" />
                      Message Teacher
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>
      </main>
      <Footer data-id="element-7466" />
    </div>;
};